import React from "react";
import Card from "../ui/Card";
import type { StatusBreakdown, StatusDetailGroup } from "../../types/performance";

type Props = {
  statusBreakdown: StatusBreakdown;
  onSelectGroup: (group: StatusDetailGroup, label: string) => void;
};

type Row = {
  key: keyof StatusBreakdown;
  label: string;
};

// Orden pensado como el flujo real de un envío: entregado -> incidencias
// conocidas -> terminales negativos -> todavía en curso -> lo que no
// sabemos interpretar. Solo se renderiza la fila si count > 0 (sección 6:
// "solamente si esas categorías existen realmente en nuestro dataset").
const ROWS: Row[] = [
  { key: "delivered", label: "Entregados" },
  { key: "absent", label: "Nadie / comprador ausente" },
  { key: "rescheduled", label: "Reprogramados" },
  { key: "delayed", label: "Demorados" },
  { key: "cancelled", label: "Cancelados" },
  { key: "rejected", label: "Rechazados" },
  { key: "inProgress", label: "En curso" },
  { key: "unknown", label: "Otros / desconocidos" },
];

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionSubtextStyle: React.CSSProperties = {
  margin: "6px 0 16px 0",
  fontSize: "14px",
  color: "var(--text-secondary)",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "560px",
};

const thStyle: React.CSSProperties = {
  textAlign: "right",
  padding: "12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
  whiteSpace: "nowrap",
};

const thLabelStyle: React.CSSProperties = { ...thStyle, textAlign: "left" };

const tdStyle: React.CSSProperties = {
  padding: "12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const tdLabelStyle: React.CSSProperties = { ...tdStyle, textAlign: "left", fontWeight: 600 };

const formatInt = (value: number) => new Intl.NumberFormat("es-AR").format(value);
const formatPercent = (rate: number) => `${(rate * 100).toFixed(1)}%`;

const PerformanceStatusBreakdownTable = ({ statusBreakdown, onSelectGroup }: Props) => {
  const visibleRows = ROWS.filter((row) => statusBreakdown[row.key].count > 0);

  return (
    <Card>
      <h2 style={sectionTitleStyle}>Desglose del período</h2>
      <p style={sectionSubtextStyle}>
        Todos los envíos del universo/período filtrado, clasificados por estado operacional real. "Antes/después de 21" es la hora del último estado registrado — para filas que no son "Entregados", no representa una entrega. Click en una fila para ver el detalle.
      </p>

      {visibleRows.length === 0 ? (
        <div style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Sin datos para este período.</div>
      ) : (
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thLabelStyle}>Estado</th>
                <th style={thStyle}>Cantidad</th>
                <th style={thStyle}>%</th>
                <th style={thStyle}>&lt;21</th>
                <th style={thStyle}>≥21</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const bucket = statusBreakdown[row.key];
                return (
                  <tr
                    key={row.key}
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelectGroup(row.key, row.label)}
                  >
                    <td style={tdLabelStyle}>{row.label}</td>
                    <td style={tdStyle}>{formatInt(bucket.count)}</td>
                    <td style={tdStyle}>{formatPercent(bucket.rate)}</td>
                    <td style={tdStyle}>{bucket.withoutHour === bucket.count ? "—" : formatInt(bucket.before21)}</td>
                    <td style={tdStyle}>{bucket.withoutHour === bucket.count ? "—" : formatInt(bucket.after21)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default PerformanceStatusBreakdownTable;
